import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';

const ROLE_OPTIONS = [
  { value: 'super_admin', label: 'Company' },
  { value: 'partner', label: 'Partner' },
  { value: 'senior_coach', label: 'Senior Coach' },
  { value: 'coach', label: 'Coach' },
  { value: 'client', label: 'Client' },
];

const ROLE_RANK = { super_admin: 0, partner: 1, senior_coach: 2, coach: 3, client: 4 };
const roleLabel = (r) => (ROLE_OPTIONS.find(o => o.value === r)?.label || r);

const badgeStyle = (role) => {
  const map = {
    super_admin: { bg: 'rgba(201,169,110,0.18)', col: 'var(--accent)' },
    partner: { bg: 'rgba(201,169,110,0.12)', col: 'var(--accent)' },
    senior_coach: { bg: 'rgba(90,140,160,0.18)', col: 'var(--info, #7fb0c4)' },
    coach: { bg: 'rgba(90,85,80,0.22)', col: 'var(--text-secondary)' },
    client: { bg: 'rgba(90,85,80,0.15)', col: 'var(--text-muted)' },
  };
  const c = map[role] || map.coach;
  return {
    fontSize: 11, padding: '2px 8px', borderRadius: 20, fontWeight: 600,
    background: c.bg, color: c.col, whiteSpace: 'nowrap',
  };
};

export default function AdminUsers() {
  const { profile, isOwner } = useAuth();
  const [users, setUsers] = useState([]);
  const [drafts, setDrafts] = useState({}); // id -> { full_name, role }
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => { if (isOwner) fetchUsers(); }, [isOwner]);

  const api = async (path, options = {}) => {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...(options.headers || {}),
      },
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
    return body;
  };

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api('/users');
      const sorted = [...data].sort((a, b) =>
        (ROLE_RANK[a.role] ?? 9) - (ROLE_RANK[b.role] ?? 9) ||
        (a.full_name || '').localeCompare(b.full_name || ''));
      setUsers(sorted);
      const d = {};
      sorted.forEach(u => { d[u.id] = { full_name: u.full_name || '', role: u.role }; });
      setDrafts(d);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const setDraft = (id, field, value) =>
    setDrafts(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));

  const isDirty = (u) => {
    const d = drafts[u.id];
    return d && (d.full_name !== (u.full_name || '') || d.role !== u.role);
  };

  const save = async (u) => {
    const d = drafts[u.id];
    setSavingId(u.id);
    setError('');
    setSuccess('');
    try {
      if (d.full_name !== (u.full_name || '')) {
        await api(`/users/${u.id}/profile`, {
          method: 'PUT', body: JSON.stringify({ full_name: d.full_name }),
        });
      }
      if (d.role !== u.role) {
        await api(`/users/${u.id}/role`, {
          method: 'PUT', body: JSON.stringify({ role: d.role }),
        });
      }
      setSuccess(`Saved changes for ${d.full_name || u.email}.`);
      await fetchUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingId(null);
    }
  };

  if (!isOwner) return <Navigate to="/partner" replace />;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 32, marginBottom: 8 }}>Manage Accounts</h1>
        <p style={{ color: 'var(--text-secondary)' }}>
          Company-only. Edit names and change roles for every account — including partners.
        </p>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}
      {success && <div className="alert alert-success" style={{ marginBottom: 16 }}>{success}</div>}

      {loading ? (
        <p style={{ color: 'var(--text-muted)' }}>Loading accounts…</p>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 12 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--text-muted)', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                <th style={{ padding: '12px 16px' }}>Name</th>
                <th style={{ padding: '12px 16px' }}>Email</th>
                <th style={{ padding: '12px 16px' }}>Current</th>
                <th style={{ padding: '12px 16px' }}>Role</th>
                <th style={{ padding: '12px 16px' }}></th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const self = u.id === profile?.id;
                const d = drafts[u.id] || { full_name: u.full_name || '', role: u.role };
                return (
                  <tr key={u.id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '10px 16px', minWidth: 180 }}>
                      <input
                        className="input"
                        value={d.full_name}
                        onChange={e => setDraft(u.id, 'full_name', e.target.value)}
                        style={{ padding: '6px 10px', fontSize: 14 }}
                      />
                    </td>
                    <td style={{ padding: '10px 16px', color: 'var(--text-secondary)', fontSize: 13 }}>
                      {u.email}{self && <span style={{ color: 'var(--accent)', marginLeft: 6 }}>(You)</span>}
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      <span style={badgeStyle(u.role)}>{roleLabel(u.role)}</span>
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      <select
                        className="input"
                        value={d.role}
                        disabled={self}
                        onChange={e => setDraft(u.id, 'role', e.target.value)}
                        style={{ padding: '6px 10px', fontSize: 14, minWidth: 140, opacity: self ? 0.5 : 1 }}
                        title={self ? 'You cannot change your own role' : ''}
                      >
                        {ROLE_OPTIONS.map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                      <button
                        className="btn btn-primary btn-sm"
                        disabled={!isDirty(u) || savingId === u.id}
                        onClick={() => save(u)}
                        style={{ opacity: !isDirty(u) ? 0.4 : 1 }}
                      >
                        {savingId === u.id ? 'Saving…' : 'Save'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
